import React, { useState, useEffect, useMemo } from 'react';
import { Search, Filter, Trash2, X, Download, Edit, Save } from 'lucide-react';
import { exportSamplesToExcel } from '../utils/excelExport';
import Input from '../components/ui/Input';
import DateInput from '../components/ui/DateInput';
import Select from '../components/ui/Select';
import { sampleService } from '../services/sampleServiceAppwrite';
import { performanceMonitor } from '../utils/performanceMonitor';
import { fuzzyFilter } from '../utils/fuzzySearch';


const Samples = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sampleToDelete, setSampleToDelete] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSample, setEditingSample] = useState(null);
  const [editFormData, setEditFormData] = useState({
    id: '',
    name: '',
    freezeDate: '',
    frozenBy: '',
    pi: '',
    passageNumber: '',
    notes: '',
    capColour: ''
  });
  
  const [sampleData, setSampleData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // NEW: Filter state
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filters, setFilters] = useState({
    dewar: '',
    rack: '',
    box: '',
    frozenBy: '',
    pi: '',
    capColour: ''
  });
  const [activeFilters, setActiveFilters] = useState({
    dewar: '',
    rack: '',
    box: '',
    frozenBy: '',
    pi: '',
    capColour: ''
  });

  // OPTIMIZATION: Track component performance
  React.useEffect(() => {
    performanceMonitor?.logComponentRender('Samples Page');
  }, []);

  // NEW: Fuzzy search with typo tolerance - REPLACED old exact match search
  const filteredSamples = React.useMemo(() => {
    if (!searchTerm) return sampleData;
    
    // Use fuzzy search that tolerates typos
    // Searches across: id, name, pi, frozenBy, and notes fields
    // Threshold 0.6 means 60% similarity required (tolerates ~2-3 character typos in average words)
    return fuzzyFilter(
      sampleData,
      searchTerm,
      ['id', 'name', 'pi', 'frozenBy', 'notes'],
      0.6
    );
  }, [sampleData, searchTerm]);

  // Load samples from Supabase on mount
  useEffect(() => {
    loadSamples();
  }, []);

  const loadSamples = async () => {
    try {
      setLoading(true);
      setError(null);

      // OPTIMIZATION: Use performance monitoring
      await performanceMonitor?.measureAsync('Samples Load', async () => {
        const data = await sampleService?.getAll();
        setSampleData(data);
      });
    } catch (err) {
      console.error('Error loading samples:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  // NEW: Helper function to get dewar and rack information from gridLocation
  const getLocationDetails = (sample) => {
    if (!sample?.gridLocation) {
      return {
        dewarName: 'N/A',
        rackName: 'N/A',
        boxName: 'N/A',
        position: 'N/A'
      };
    }

    // Use the rack_number from the database instead of extracting from ID
    let cleanRackName = 'Unknown';
    if (sample?.gridLocation?.rackNumber != null) {
      cleanRackName = `Rack ${sample?.gridLocation?.rackNumber}`;
    }

    // Extract only the box number, removing any extra characters
    let cleanBoxName = sample?.gridLocation?.boxName || 'Unknown';
    if (cleanBoxName !== 'Unknown' && cleanBoxName !== 'N/A') {
      // Remove everything after the first space, dash, or underscore
      // This will convert "Box 1 - extra" or "Box1-extra" to just "Box 1" or "Box1"
      cleanBoxName = cleanBoxName?.split(/[\s\-_]/)?.[0];
    }

    return {
      dewarName: sample?.gridLocation?.dewarId ? 'Dewar' : 'Unknown',
      rackName: cleanRackName,
      boxName: cleanBoxName,
      position: sample?.gridLocation?.position || 'Unknown'
    };
  };

  const capColours = {
    red: 'bg-red-500',
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    purple: 'bg-purple-500',
    orange: 'bg-orange-500',
    pink: 'bg-pink-500',
    teal: 'bg-teal-500',
    indigo: 'bg-indigo-500',
    cyan: 'bg-cyan-500',
    lime: 'bg-lime-500',
    amber: 'bg-amber-500',
    grey: 'bg-gray-500',
    silver: 'bg-gray-400',
    white: 'bg-white border border-gray-300'
  };

  const capColourOptions = [
    { value: '', label: 'All Colors' },
    { value: 'red', label: 'Red' },
    { value: 'blue', label: 'Blue' },
    { value: 'green', label: 'Green' },
    { value: 'yellow', label: 'Yellow' },
    { value: 'purple', label: 'Purple' },
    { value: 'orange', label: 'Orange' },
    { value: 'pink', label: 'Pink' },
    { value: 'teal', label: 'Teal' },
    { value: 'indigo', label: 'Indigo' },
    { value: 'cyan', label: 'Cyan' },
    { value: 'lime', label: 'Lime' },
    { value: 'amber', label: 'Amber' },
    { value: 'grey', label: 'Grey' },
    { value: 'silver', label: 'Silver' },
    { value: 'white', label: 'White' }
  ];

  // NEW: Get unique filter options from sample data
  const getUniqueFilterOptions = () => {
    const dewars = new Set();
    const racks = new Set();
    const boxes = new Set();
    const frozenByList = new Set();
    const piList = new Set();

    sampleData?.forEach(sample => {
      const location = getLocationDetails(sample);
      if (location?.dewarName && location?.dewarName !== 'N/A' && location?.dewarName !== 'Unknown') {
        dewars?.add(location?.dewarName);
      }
      if (location?.rackName && location?.rackName !== 'N/A' && location?.rackName !== 'Unknown') {
        racks?.add(location?.rackName);
      }
      if (location?.boxName && location?.boxName !== 'N/A' && location?.boxName !== 'Unknown') {
        boxes?.add(location?.boxName);
      }
      if (sample?.frozenBy) {
        frozenByList?.add(sample?.frozenBy);
      }
      if (sample?.pi) {
        piList?.add(sample?.pi);
      }
    });

    return {
      dewars: [{ value: '', label: 'All Dewars' }, ...Array.from(dewars)?.map(d => ({ value: d, label: d }))],
      racks: [{ value: '', label: 'All Racks' }, ...Array.from(racks)?.map(r => ({ value: r, label: r }))],
      boxes: [{ value: '', label: 'All Boxes' }, ...Array.from(boxes)?.map(b => ({ value: b, label: b }))],
      frozenBy: [{ value: '', label: 'All Users' }, ...Array.from(frozenByList)?.map(f => ({ value: f, label: f }))],
      pi: [{ value: '', label: 'All PIs' }, ...Array.from(piList)?.map(p => ({ value: p, label: p }))]
    };
  };

  // NEW: Filter samples based on active filters and search term with fuzzy search
  const getFilteredSamples = () => {
    return sampleData?.filter(sample => {
      const location = getLocationDetails(sample);
      
      // Apply fuzzy search term filter - REPLACED old exact match
      let matchesSearch = !searchTerm;
      if (searchTerm) {
        // Use fuzzy filter for typo-tolerant search
        const fuzzyResults = fuzzyFilter(
          [sample],
          searchTerm,
          ['id', 'name', 'frozenBy', 'pi', 'notes'],
          0.6
        );
        matchesSearch = fuzzyResults?.length > 0;
      }

      // Apply individual filters
      const matchesDewar = !activeFilters?.dewar || location?.dewarName === activeFilters?.dewar;
      const matchesRack = !activeFilters?.rack || location?.rackName === activeFilters?.rack;
      const matchesBox = !activeFilters?.box || location?.boxName === activeFilters?.box;
      const matchesFrozenBy = !activeFilters?.frozenBy || sample?.frozenBy === activeFilters?.frozenBy;
      const matchesPI = !activeFilters?.pi || sample?.pi === activeFilters?.pi;
      const matchesCapColour = !activeFilters?.capColour || sample?.capColour === activeFilters?.capColour;

      return matchesSearch && matchesDewar && matchesRack && matchesBox && 
             matchesFrozenBy && matchesPI && matchesCapColour;
    });
  };

  // NEW: Handle filter modal
  const handleFilterClick = () => {
    setFilters(activeFilters);
    setShowFilterModal(true);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilters = () => {
    setActiveFilters(filters);
    setShowFilterModal(false);
  };

  const handleClearFilters = () => {
    const emptyFilters = {
      dewar: '',
      rack: '',
      box: '',
      frozenBy: '',
      pi: '',
      capColour: ''
    };
    setFilters(emptyFilters);
    setActiveFilters(emptyFilters);
    setShowFilterModal(false);
  };

  const handleFilterCancel = () => {
    setFilters(activeFilters);
    setShowFilterModal(false);
  };

  // NEW: Check if any filters are active
  const hasActiveFilters = () => {
    return Object.values(activeFilters)?.some(value => value !== '');
  };

  const handleEditClick = (sample) => {
    setEditingSample(sample);
    setEditFormData({
      id: sample?.id || '',
      name: sample?.name || '',
      freezeDate: sample?.freezeDate || '',
      frozenBy: sample?.frozenBy || '',
      pi: sample?.pi || '',
      passageNumber: sample?.passageNumber || '',
      notes: sample?.notes || '',
      capColour: sample?.capColour || ''
    });
    setShowEditModal(true);
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditSave = async () => {
    if (!editingSample) return;

    try {
      setError('');
      await sampleService?.update(editingSample?.id, {
        name: editFormData?.name,
        freezeDate: editFormData?.freezeDate,
        frozenBy: editFormData?.frozenBy,
        pi: editFormData?.pi,
        passageNumber: editFormData?.passageNumber,
        notes: editFormData?.notes,
        capColor: editFormData?.capColour
      });
      
      await loadSamples();
      setShowEditModal(false);
      setEditingSample(null);
      setEditFormData({
        id: '',
        name: '',
        freezeDate: '',
        frozenBy: '',
        pi: '',
        passageNumber: '',
        notes: '',
        capColour: ''
      });
    } catch (err) {
      console.error('Error updating sample:', err);
      setError(err?.message || 'Failed to update sample');
    }
  };

  const handleEditCancel = () => {
    setShowEditModal(false);
    setEditingSample(null);
    setEditFormData({
      id: '',
      name: '',
      freezeDate: '',
      frozenBy: '',
      pi: '',
      passageNumber: '',
      notes: '',
      capColour: ''
    });
  };

  const handleDeleteClick = (sample) => {
    setSampleToDelete(sample);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!sampleToDelete) return;

    try {
      setError('');
      await sampleService?.delete(sampleToDelete?.id);
      await loadSamples();
      setShowDeleteModal(false);
      setSampleToDelete(null);
    } catch (err) {
      console.error('Error deleting sample:', err);
      setError(err?.message || 'Failed to delete sample');
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setSampleToDelete(null);
  };

  const filterOptions = getUniqueFilterOptions();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading samples...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8">
          <div className="mb-4 sm:mb-0">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Samples</h1>
            <p className="mt-2 text-sm sm:text-base text-gray-600">Manage laboratory samples</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => exportSamplesToExcel(sampleData)}
              className="flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm sm:text-base"
            >
              <Download className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Export to Excel
            </button>
          </div>
        </div>

        {/* Search and Filter - Responsive */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type="text"
                placeholder="Search samples... (typo-tolerant)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm sm:text-base"
              />
            </div>
            <button 
              onClick={handleFilterClick}
              className={`flex items-center justify-center px-4 py-2 border rounded-lg transition-colors text-sm sm:text-base ${
                hasActiveFilters() 
                  ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700' :'border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Filter className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Filter {hasActiveFilters() && `(${Object.values(activeFilters)?.filter(v => v)?.length})`}
            </button>
          </div>
          
          {/* Active Filters Display */}
          {hasActiveFilters() && (
            <div className="mt-3 flex flex-wrap gap-2">
              {Object.entries(activeFilters)?.map(([key, value]) => 
                value && (
                  <div key={key} className="flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm">
                    <span className="capitalize">{key}: {value}</span>
                    <button
                      onClick={() => {
                        const newFilters = { ...activeFilters, [key]: '' };
                        setActiveFilters(newFilters);
                        setFilters(newFilters);
                      }}
                      className="ml-1 hover:text-indigo-900"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )
              )}
            </div>
          )}
        </div>

        {/* Samples Table - Responsive */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Desktop View */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cap Colour</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dewar</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rack</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Box</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Position</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Freeze Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Frozen By</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PI</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Passage #</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSamples?.map((sample) => {
                  const location = getLocationDetails(sample);
                  return (
                    <tr key={sample?.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditClick(sample)}
                            className="text-indigo-600 hover:text-indigo-900 transition-colors"
                            title="Edit sample"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(sample)}
                            className="text-red-600 hover:text-red-900 transition-colors"
                            title="Delete sample"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className={`w-6 h-6 rounded-full ${capColours?.[sample?.capColour] || 'bg-gray-300'}`}></div>
                          <span className="text-sm text-gray-900 capitalize">{sample?.capColour}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{sample?.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{location?.dewarName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{location?.rackName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{location?.boxName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{location?.position}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{sample?.freezeDate}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{sample?.frozenBy}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{sample?.pi}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{sample?.passageNumber}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">{sample?.notes}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile View - Cards */}
          <div className="lg:hidden p-4 space-y-4">
            {filteredSamples?.map((sample) => {
              const location = getLocationDetails(sample);
              return (
                <div key={sample?.id} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-semibold text-gray-900">{sample?.name}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditClick(sample)}
                        className="text-indigo-600 hover:text-indigo-900 transition-colors"
                        title="Edit sample"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(sample)}
                        className="text-red-600 hover:text-red-900 transition-colors"
                        title="Delete sample"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="grid grid-cols-2 gap-2 pb-2 border-b border-gray-200">
                      <div>
                        <span className="text-gray-500">Dewar:</span>
                        <p className="font-medium text-gray-900">{location?.dewarName}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Rack:</span>
                        <p className="font-medium text-gray-900">{location?.rackName}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Box:</span>
                        <p className="font-medium text-gray-900">{location?.boxName}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Position:</span>
                        <p className="font-medium text-gray-900">{location?.position}</p>
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Freeze Date:</span>
                      <span className="font-medium text-gray-900">{sample?.freezeDate}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Frozen By:</span>
                      <span className="font-medium text-gray-900">{sample?.frozenBy}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">PI:</span>
                      <span className="font-medium text-gray-900">{sample?.pi}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Passage Number:</span>
                      <span className="font-medium text-gray-900">{sample?.passageNumber}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Cap Colour:</span>
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded-full ${capColours?.[sample?.capColour] || 'bg-gray-300'}`}></div>
                        <span className="font-medium text-gray-900 capitalize">{sample?.capColour}</span>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-gray-200">
                      <span className="text-gray-500">Notes:</span>
                      <p className="font-medium text-gray-900 mt-1">{sample?.notes}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* No Results Message */}
          {filteredSamples?.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <p className="text-lg font-medium">No samples found</p>
              <p className="text-sm mt-2">Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      </div>
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Delete Sample</h3>
              <button
                onClick={handleDeleteCancel}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete sample <span className="font-semibold">{sampleToDelete?.id}</span> ({sampleToDelete?.name})? 
              This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Edit Sample Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Edit Sample</h3>
              <button
                onClick={handleEditCancel}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <Input
                label="Sample ID"
                value={editFormData?.id}
                onChange={(e) => handleEditFormChange('id', e?.target?.value)}
                placeholder="Enter sample ID"
                required
                disabled
              />

              <Input
                label="Name"
                value={editFormData?.name}
                onChange={(e) => handleEditFormChange('name', e?.target?.value)}
                placeholder="Enter sample name"
                required
              />

              <DateInput
                label="Freeze Date"
                value={editFormData?.freezeDate}
                onChange={(e) => handleEditFormChange('freezeDate', e?.target?.value)}
                placeholder="DD-MM-YYYY"
                required
              />

              <Input
                label="Frozen By"
                value={editFormData?.frozenBy}
                onChange={(e) => handleEditFormChange('frozenBy', e?.target?.value)}
                placeholder="Enter who froze the sample"
                required
              />

              <Input
                label="PI"
                value={editFormData?.pi}
                onChange={(e) => handleEditFormChange('pi', e?.target?.value)}
                placeholder="Enter PI information"
              />

              <Input
                label="Passage Number"
                value={editFormData?.passageNumber}
                onChange={(e) => handleEditFormChange('passageNumber', e?.target?.value)}
                placeholder="Enter passage number"
              />

              <Select
                label="Cap Colour"
                value={editFormData?.capColour}
                onChange={(value) => handleEditFormChange('capColour', value)}
                options={capColourOptions}
                placeholder="Select cap colour"
                required
              />

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-900">Notes</label>
                <textarea
                  value={editFormData?.notes}
                  onChange={(e) => handleEditFormChange('notes', e?.target?.value)}
                  placeholder="Enter additional notes"
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm resize-none"
                />
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex gap-3 justify-end border-t border-gray-200">
              <button
                onClick={handleEditCancel}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSave}
                className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
      {/* NEW: Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Filter Samples</h3>
              <button
                onClick={handleFilterCancel}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <Select
                label="Dewar"
                value={filters?.dewar}
                onChange={(value) => handleFilterChange('dewar', value)}
                options={filterOptions?.dewars}
                placeholder="Select dewar"
              />

              <Select
                label="Rack"
                value={filters?.rack}
                onChange={(value) => handleFilterChange('rack', value)}
                options={filterOptions?.racks}
                placeholder="Select rack"
              />

              <Select
                label="Box"
                value={filters?.box}
                onChange={(value) => handleFilterChange('box', value)}
                options={filterOptions?.boxes}
                placeholder="Select box"
              />

              <Select
                label="Frozen By"
                value={filters?.frozenBy}
                onChange={(value) => handleFilterChange('frozenBy', value)}
                options={filterOptions?.frozenBy}
                placeholder="Select user"
              />

              <Select
                label="PI"
                value={filters?.pi}
                onChange={(value) => handleFilterChange('pi', value)}
                options={filterOptions?.pi}
                placeholder="Select PI"
              />

              <Select
                label="Cap Colour"
                value={filters?.capColour}
                onChange={(value) => handleFilterChange('capColour', value)}
                options={capColourOptions}
                placeholder="Select cap colour"
              />
            </div>

            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex gap-3 justify-end border-t border-gray-200">
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Clear All
              </button>
              <button
                onClick={handleFilterCancel}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleApplyFilters}
                className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <Filter className="w-4 h-4 mr-2" />
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Samples;